import { useCallback, useState } from "react";
import useShowError from "@/shared/custom-hook/useShowError.ts";
import { useTranslation } from "react-i18next";
import usePrintScheduleTimeSlotsFunction from "@/pages/human-resources/time-management/schedules/site-schedule/day-view/model/printing/usePrintScheduleTimeSlots.ts";
import { noop } from "@/shared/utils/Events";
import type { PrintMenuItem } from "@/pages/human-resources/time-management/components/print-menu/PrintMenuItem";
import usePrintDailyScheduleCells from "@/pages/human-resources/time-management/services/hooks/usePrintDailyScheduleCells";
import { useWithThreeDotsOverlay } from "@/shared/components/generic/hooks/useWithThreeDotsOverlay.ts";

type Action = "printTimeSlots" | "printCells";

interface Controller {
  menuItems: PrintMenuItem<Action>[];
  runAction: (action: Action) => void;
}

export default function useDailySchedulePrintMenuController(): Controller {
  const withThreeDotsOverlay = useWithThreeDotsOverlay();
  const { t } = useTranslation(["humanResources"], {
    keyPrefix: "timeManagement",
  });
  const [printFailed, setPrintFailed] = useState(false);
  useShowError(printFailed, t("print.error"));
  const printCells = usePrintDailyScheduleCells();

  const menuItems: PrintMenuItem<Action>[] = [
    {
      action: "printTimeSlots",
      label: t("print.dailySchedule.printTimeSlots"),
    },
    {
      action: "printCells",
      label: t("print.dailySchedule.printCells"),
    },
  ];

  const printTimeSlots = usePrintScheduleTimeSlotsFunction({
    onAfterPrint: noop,
    onPrintError: () => {
      setPrintFailed(true);
    },
  });

  const runAction = useCallback(
    (action: Action): void => {
      setPrintFailed(false);
      const handleSuccess = (success: boolean): void => {
        setPrintFailed(!success);
      };
      switch (action) {
        case "printTimeSlots":
          printTimeSlots();
          break;
        case "printCells":
          void withThreeDotsOverlay(async () => {
            handleSuccess(await printCells());
          });
          break;
      }
    },
    [printCells, printTimeSlots, withThreeDotsOverlay],
  );

  return {
    menuItems,
    runAction,
  };
}
